import React, { useState, useEffect, useRef } from 'react'
import { FilesetResolver, GestureRecognizer } from '@mediapipe/tasks-vision'
import * as XLSX from 'xlsx'
import confetti from 'canvas-confetti'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import gsap from 'gsap'
import { Volume2, VolumeX, Upload, Play, Download } from 'lucide-react'
import Navigation from '../../components/common/Navigation'

// 3D Scene Class (exact copy from 3d-scene.js)
class Scene3D {
    container: HTMLDivElement
    names: string[]
    particles: THREE.Points | null
    raycaster: THREE.Raycaster
    pointer: THREE.Vector2
    INTERSECTED: any
    clock: THREE.Clock
    targets: { sphere: Float32Array; tree: Float32Array }
    scene!: THREE.Scene
    camera!: THREE.PerspectiveCamera
    renderer!: THREE.WebGLRenderer
    controls!: OrbitControls
    resizeObserver!: ResizeObserver
    currentShape!: string
    originalColors!: Float32Array
    originalSizes!: Float32Array
    highlightedParticleIndex: number | null
    activeTween: any

    constructor(container: HTMLDivElement) {
        this.container = container
        this.names = []
        this.particles = null
        this.raycaster = new THREE.Raycaster()
        this.pointer = new THREE.Vector2()
        this.INTERSECTED = null
        this.clock = new THREE.Clock()
        this.highlightedParticleIndex = null
        this.activeTween = null

        this.targets = {
            sphere: new Float32Array(),
            tree: new Float32Array()
        }

        this.init()
        this.animate()
    }

    init() {
        this.scene = new THREE.Scene()
        this.scene.background = new THREE.Color(0x050510)
        this.scene.fog = new THREE.FogExp2(0x050510, 0.002)

        const width = this.container.clientWidth || window.innerWidth
        const height = this.container.clientHeight || window.innerHeight

        this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000)
        this.camera.position.z = 100

        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
        this.renderer.setSize(width, height)
        this.renderer.setPixelRatio(window.devicePixelRatio)
        this.container.appendChild(this.renderer.domElement)

        this.controls = new OrbitControls(this.camera, this.renderer.domElement)
        this.controls.enableDamping = true
        this.controls.dampingFactor = 0.05
        this.controls.autoRotate = true
        this.controls.autoRotateSpeed = 0.5

        this.raycaster.params.Points!.threshold = 2

        this.resizeObserver = new ResizeObserver(() => this.onWindowResize())
        this.resizeObserver.observe(this.container)

        window.addEventListener('pointermove', this.onPointerMove.bind(this))
    }

    createParticles(names: string[]) {
        if (!names || names.length === 0) return
        this.names = names

        if (this.particles) {
            this.scene.remove(this.particles)
            this.particles.geometry.dispose()
                ; (this.particles.material as THREE.Material).dispose()
        }

        const count = names.length
        const geometry = new THREE.BufferGeometry()
        const positions = new Float32Array(count * 3)
        const colors = new Float32Array(count * 3)
        const sizes = new Float32Array(count)

        this.targets.sphere = new Float32Array(count * 3)
        this.targets.tree = new Float32Array(count * 3)

        const color = new THREE.Color()
        const range = count

        for (let i = 0; i < count; i++) {
            // Sphere Shape
            const phi = Math.acos(-1 + (2 * i) / range)
            const theta = Math.sqrt(range * Math.PI) * phi
            const r = 35 + Math.random() * 45

            const sphereX = r * Math.cos(theta) * Math.sin(phi)
            const sphereY = r * Math.sin(theta) * Math.sin(phi)
            const sphereZ = r * Math.cos(phi)

            this.targets.sphere[i * 3] = sphereX
            this.targets.sphere[i * 3 + 1] = sphereY
            this.targets.sphere[i * 3 + 2] = sphereZ

            positions[i * 3] = sphereX
            positions[i * 3 + 1] = sphereY
            positions[i * 3 + 2] = sphereZ

            // Tree Shape
            const h = 120
            const y = ((i / range) * h) - (h / 2)
            const normalizedY = (y + h / 2) / h
            const coneR = 50 * (1 - normalizedY)
            const angle = i * 0.5

            this.targets.tree[i * 3] = coneR * Math.cos(angle)
            this.targets.tree[i * 3 + 1] = y
            this.targets.tree[i * 3 + 2] = coneR * Math.sin(angle)

            // Colors
            color.setHSL(0.1 + Math.random() * 0.1, 0.8, 0.6)
            colors[i * 3] = color.r
            colors[i * 3 + 1] = color.g
            colors[i * 3 + 2] = color.b

            sizes[i] = 4.0 + Math.random() * 10.0
        }

        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
        geometry.setAttribute('pSize', new THREE.BufferAttribute(sizes, 1))

        // Create texture
        const canvas = document.createElement('canvas')
        canvas.width = 64
        canvas.height = 64
        const context = canvas.getContext('2d')!

        const gradient = context.createRadialGradient(32, 32, 0, 32, 32, 32)
        gradient.addColorStop(0, 'rgba(255, 255, 255, 1)')
        gradient.addColorStop(0.2, 'rgba(255, 240, 200, 0.9)')
        gradient.addColorStop(0.5, 'rgba(255, 200, 100, 0.4)')
        gradient.addColorStop(1, 'rgba(255, 150, 50, 0)')

        context.fillStyle = gradient
        context.fillRect(0, 0, 64, 64)
        const texture = new THREE.CanvasTexture(canvas)

        const material = new THREE.PointsMaterial({
            size: 1.0,
            map: texture,
            vertexColors: true,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
            transparent: true,
            opacity: 1.0
        })

        material.onBeforeCompile = (shader) => {
            shader.vertexShader = `attribute float pSize;\n` + shader.vertexShader
            shader.vertexShader = shader.vertexShader.replace(
                'gl_PointSize = size;',
                'gl_PointSize = pSize;'
            )
        }

        this.particles = new THREE.Points(geometry, material)
        this.scene.add(this.particles)

        this.currentShape = 'sphere'
        this.originalColors = new Float32Array(colors)
        this.originalSizes = new Float32Array(sizes)
        this.highlightedParticleIndex = null
    }

    transformTo(shape: 'sphere' | 'tree') {
        if (!this.particles) return
        this.currentShape = shape

        const targetArr = this.targets[shape]
        const geometry = this.particles.geometry
        const attributes = geometry.attributes

        const proxy = { t: 0 }
        const startPositions = new Float32Array(attributes.position.array as Float32Array)

        if (this.activeTween) {
            this.activeTween.kill()
        }

        this.activeTween = gsap.to(proxy, {
            t: 1,
            duration: 0.3,
            ease: "power2.out",
            onUpdate: () => {
                const pos = attributes.position.array as Float32Array
                for (let i = 0; i < pos.length; i++) {
                    pos[i] = startPositions[i] + (targetArr[i] - startPositions[i]) * proxy.t
                }
                attributes.position.needsUpdate = true
            },
            onComplete: () => {
                this.activeTween = null
            }
        })
    }

    rotateScene(deltaX: number, deltaY: number) {
        this.scene.rotation.y += deltaX * 0.1
        this.scene.rotation.x += deltaY * 0.1
    }

    setAutoRotate(enabled: boolean) {
        if (this.controls) {
            this.controls.autoRotate = enabled
        }
    }

    highlightParticle(index: number, highlightColor = new THREE.Color(0xffffff), highlightSize = 15) {
        if (!this.particles || index === undefined || index < 0 || index >= this.names.length) {
            return
        }

        const colors = this.particles.geometry.attributes.color.array as Float32Array
        const sizes = this.particles.geometry.attributes.pSize.array as Float32Array

        if (this.highlightedParticleIndex !== null && this.highlightedParticleIndex !== index) {
            const prevIndex = this.highlightedParticleIndex
            colors[prevIndex * 3] = this.originalColors[prevIndex * 3]
            colors[prevIndex * 3 + 1] = this.originalColors[prevIndex * 3 + 1]
            colors[prevIndex * 3 + 2] = this.originalColors[prevIndex * 3 + 2]
            sizes[prevIndex] = this.originalSizes[prevIndex]
        }

        colors[index * 3] = highlightColor.r
        colors[index * 3 + 1] = highlightColor.g
        colors[index * 3 + 2] = highlightColor.b
        sizes[index] = highlightSize

        this.particles.geometry.attributes.color.needsUpdate = true
        this.particles.geometry.attributes.pSize.needsUpdate = true

        this.highlightedParticleIndex = index
    }

    clearHighlight() {
        if (this.highlightedParticleIndex !== null) {
            if (!this.particles) return

            const prevIndex = this.highlightedParticleIndex
            const colors = this.particles.geometry.attributes.color.array as Float32Array
            const sizes = this.particles.geometry.attributes.pSize.array as Float32Array

            if (this.originalColors && this.originalSizes) {
                colors[prevIndex * 3] = this.originalColors[prevIndex * 3]
                colors[prevIndex * 3 + 1] = this.originalColors[prevIndex * 3 + 1]
                colors[prevIndex * 3 + 2] = this.originalColors[prevIndex * 3 + 2]
                sizes[prevIndex] = this.originalSizes[prevIndex]

                this.particles.geometry.attributes.color.needsUpdate = true
                this.particles.geometry.attributes.pSize.needsUpdate = true
            }

            this.highlightedParticleIndex = null
        }
    }

    onWindowResize() {
        if (!this.container) return
        const width = this.container.clientWidth
        const height = this.container.clientHeight

        if (this.camera) {
            this.camera.aspect = width / height
            this.camera.updateProjectionMatrix()
        }
        if (this.renderer) {
            this.renderer.setSize(width, height)
        }
    }

    onPointerMove(event: PointerEvent) {
        if (!this.container) return
        const rect = this.container.getBoundingClientRect()
        this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
        this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
    }

    animate() {
        requestAnimationFrame(this.animate.bind(this))

        if (this.controls) {
            this.controls.update()
        }

        this.renderer.render(this.scene, this.camera)
    }

    destroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect()
        }
        if (this.particles) {
            this.scene.remove(this.particles)
            this.particles.geometry.dispose()
                ; (this.particles.material as THREE.Material).dispose()
        }
        if (this.renderer) {
            this.renderer.dispose()
            if (this.renderer.domElement.parentElement) {
                this.renderer.domElement.parentElement.removeChild(this.renderer.domElement)
            }
        }
    }
}

const RandomNamePicker = () => {
    const [names, setNames] = useState<string[]>([])
    const [textareaValue, setTextareaValue] = useState('')
    const [isRunning, setIsRunning] = useState(false)
    const [isShuffling, setIsShuffling] = useState(false)
    const [selectedName, setSelectedName] = useState<string | null>(null)
    const [soundEnabled, setSoundEnabled] = useState(true)
    const [gestureRecognizer, setGestureRecognizer] = useState<any>(null)
    const [webcamRunning, setWebcamRunning] = useState(false)
    const webcamRunningRef = useRef(false)
    const [statusText, setStatusText] = useState('Waiting for names...')
    const [gestureIndicator, setGestureIndicator] = useState('👋 Raise hand to shuffle')
    const [indicatorColor, setIndicatorColor] = useState('#ccc')
    const [lastGesture, setLastGesture] = useState('None')
    const [isSelecting, setIsSelecting] = useState(false)
    const [lastSelectedIndex, setLastSelectedIndex] = useState<number | undefined>(undefined)

    const videoRef = useRef<HTMLVideoElement>(null)
    const audioCtxRef = useRef<AudioContext | null>(null)
    const scene3dRef = useRef<Scene3D | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const animationFrameRef = useRef<number>()
    const gestureCountRef = useRef(0)
    const lastGestureRef = useRef('None')
    const isSelectingRef = useRef(false)
    const isShufflingRef = useRef(false)

    // Audio buffers for loaded MP3 files
    const audioBuffersRef = useRef<{ suspense: AudioBuffer | null; winner: AudioBuffer | null }>({
        suspense: null,
        winner: null
    })
    const activeSourcesRef = useRef<{ suspense: AudioBufferSourceNode | null }>({
        suspense: null
    })

    // Initialize Audio Context
    useEffect(() => {
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()

        // Load audio files
        const loadAudio = async () => {
            try {
                const suspenseRes = await fetch('/audio/suspense.mp3')
                if (suspenseRes.ok) {
                    const arrayBuffer = await suspenseRes.arrayBuffer()
                    audioBuffersRef.current.suspense = await audioCtxRef.current!.decodeAudioData(arrayBuffer)
                    console.log('Loaded suspense.mp3')
                }

                const winnerRes = await fetch('/audio/winner.mp3')
                if (winnerRes.ok) {
                    const arrayBuffer = await winnerRes.arrayBuffer()
                    audioBuffersRef.current.winner = await audioCtxRef.current!.decodeAudioData(arrayBuffer)
                    console.log('Loaded winner.mp3')
                }
            } catch (e) {
                console.log('Audio files not found, using fallback')
            }
        }
        loadAudio()

        return () => {
            stopHeartbeatLoop()
            audioCtxRef.current?.close()
        }
    }, [])

    // Initialize 3D Scene - deferred until container is ready
    const initScene3D = () => {
        if (containerRef.current && !scene3dRef.current) {
            try {
                scene3dRef.current = new Scene3D(containerRef.current)
                console.log('3D Scene initialized successfully')
            } catch (error) {
                console.error('Failed to initialize 3D scene:', error)
            }
        }
    }

    // Cleanup 3D scene on unmount
    useEffect(() => {
        return () => {
            if (scene3dRef.current) {
                scene3dRef.current.destroy()
                scene3dRef.current = null
            }
        }
    }, [])

    // Initialize MediaPipe
    useEffect(() => {
        const initMediaPipe = async () => {
            try {
                setStatusText('Initializing AI...')
                const vision = await FilesetResolver.forVisionTasks(
                    'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm'
                )
                const recognizer = await GestureRecognizer.createFromOptions(vision, {
                    baseOptions: {
                        modelAssetPath:
                            'https://storage.googleapis.com/mediapipe-models/gesture_recognizer/gesture_recognizer/float16/1/gesture_recognizer.task',
                        delegate: 'GPU'
                    },
                    runningMode: 'VIDEO',
                    numHands: 1
                })
                setGestureRecognizer(recognizer)
                setStatusText('Waiting for players...')
            } catch (error) {
                console.error(error)
                setStatusText('AI Load Error')
            }
        }
        initMediaPipe()
    }, [])

    // Parse names from text
    const parseNames = (text: string) => {
        return text
            .split('\n')
            .map((n) => n.trim())
            .filter((n) => n.length > 0)
    }

    // Handle file upload
    const handleFile = (file: File) => {
        const reader = new FileReader()
        reader.onload = (e) => {
            const data = new Uint8Array(e.target?.result as ArrayBuffer)
            const workbook = XLSX.read(data, { type: 'array' })
            const rows = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], {
                header: 1
            }) as any[]
            const extractedNames = rows.flat().filter((n) => n && typeof n === 'string')
            if (extractedNames.length > 0) {
                setNames(extractedNames)
                setTextareaValue(extractedNames.join('\n'))
                setStatusText(`${extractedNames.length} names ready`)
            }
        }
        reader.readAsArrayBuffer(file)
    }

    // Sound effects
    const startHeartbeatLoop = () => {
        if (!soundEnabled || !audioCtxRef.current) return

        if (activeSourcesRef.current.suspense) {
            return // Already playing
        }

        if (audioBuffersRef.current.suspense) {
            const source = audioCtxRef.current.createBufferSource()
            source.buffer = audioBuffersRef.current.suspense
            source.loop = true
            const gain = audioCtxRef.current.createGain()
            gain.gain.value = 0.5
            source.connect(gain)
            gain.connect(audioCtxRef.current.destination)
            source.start()
            activeSourcesRef.current.suspense = source
        }
    }

    const stopHeartbeatLoop = () => {
        if (activeSourcesRef.current.suspense) {
            try {
                activeSourcesRef.current.suspense.stop()
            } catch (e) { }
            activeSourcesRef.current.suspense = null
        }
    }

    const playWinnerSound = () => {
        console.log('🔊 playWinnerSound called:', { soundEnabled, hasAudioCtx: !!audioCtxRef.current, hasBuffer: !!audioBuffersRef.current.winner })
        if (!soundEnabled || !audioCtxRef.current) return

        stopHeartbeatLoop()

        if (audioBuffersRef.current.winner) {
            console.log('🎵 Playing winner sound!')
            const source = audioCtxRef.current.createBufferSource()
            source.buffer = audioBuffersRef.current.winner
            const gain = audioCtxRef.current.createGain()
            gain.gain.value = 0.8
            source.connect(gain)
            gain.connect(audioCtxRef.current.destination)
            source.start()
        } else {
            console.log('⚠️ No winner audio buffer loaded')
        }
    }

    // Start chaos animation
    const startChaosAnimation = () => {
        if (!scene3dRef.current) return

        setSelectedName(null)
        scene3dRef.current.clearHighlight()

        const pool = names.length > 0 ? [...names] : ['Player 1', 'Player 2', 'Player 3', 'Player 4', 'Player 5']

        while (pool.length < 50) {
            pool.push(...(names.length > 0 ? names : ['Player ' + Math.floor(Math.random() * 100)]))
        }

        scene3dRef.current.createParticles(pool)
        scene3dRef.current.transformTo('sphere')
        startHeartbeatLoop()
    }

    // Start webcam
    const startWebcam = async () => {
        if (!videoRef.current || webcamRunning) return
        if (names.length < 2) {
            alert('Need at least 2 names!')
            return
        }

        if (!gestureRecognizer) {
            alert('AI not ready yet, please wait...')
            return
        }

        // Initialize 3D scene if not already done
        initScene3D()

        try {
            console.log('🎥 Requesting camera access...')
            const stream = await navigator.mediaDevices.getUserMedia({ video: true })
            videoRef.current.srcObject = stream

            // Wait for video to be ready before starting recognition
            videoRef.current.addEventListener('loadeddata', () => {
                console.log('📹 Video loaded, starting gesture recognition...')
                webcamRunningRef.current = true
                setWebcamRunning(true)
                predictWebcam()
            }, { once: true })

            if (audioCtxRef.current?.state === 'suspended') {
                audioCtxRef.current.resume()
            }
            setStatusText('Listening for Gestures...')
            setIsRunning(true)
            isShufflingRef.current = true
            setIsShuffling(true)

            startChaosAnimation()
        } catch (error) {
            console.error('Webcam error:', error)
            setStatusText('Camera access denied')
        }
    }

    const predictWebcam = () => {
        if (!videoRef.current || !gestureRecognizer || !webcamRunningRef.current) {
            console.log('⚠️ PredictWebcam stopped:', { video: !!videoRef.current, recognizer: !!gestureRecognizer, running: webcamRunningRef.current })
            return
        }

        const now = Date.now()
        if ((videoRef.current as any).oldTime !== videoRef.current.currentTime) {
            (videoRef.current as any).oldTime = videoRef.current.currentTime
            const results = gestureRecognizer.recognizeForVideo(videoRef.current, now)

            if (results.landmarks && results.landmarks.length > 0) {
                const landmarks = results.landmarks[0]
                const x = landmarks[0].x
                const y = landmarks[0].y

                if (scene3dRef.current && scene3dRef.current.rotateScene) {
                    scene3dRef.current.rotateScene((x - 0.5) * 4, (y - 0.5) * 4)
                    if (scene3dRef.current.setAutoRotate) scene3dRef.current.setAutoRotate(false)
                }

                if (results.gestures.length > 0) {
                    const gesture = results.gestures[0][0]
                    if (gesture.score > 0.5) processGesture(gesture.categoryName)
                } else {
                    processGesture('None')
                }
            } else {
                if (scene3dRef.current && scene3dRef.current.setAutoRotate) {
                    scene3dRef.current.setAutoRotate(true)
                }
                processGesture('None')
            }

            // Random selection on Pointing_Up
            if (isShufflingRef.current && lastGestureRef.current === 'Pointing_Up') {
                console.log('👆 Attempting to pick name...', { isShuffling: isShufflingRef.current, isSelecting: isSelectingRef.current, namesCount: names.length })
                if (!isSelectingRef.current && names.length > 0) {
                    isSelectingRef.current = true
                    setIsSelecting(true)
                    console.log('✅ Picking a winner!')

                    let pool = [...Array(names.length).keys()]
                    if (lastSelectedIndex !== undefined && names.length > 1) {
                        pool = pool.filter((i) => i !== lastSelectedIndex)
                    }

                    const randomIndex = pool[Math.floor(Math.random() * pool.length)]
                    const winnerName = names[randomIndex]
                    console.log('🏆 Winner:', winnerName)

                    setLastSelectedIndex(randomIndex)
                    setSelectedName(winnerName)

                    if (scene3dRef.current) {
                        scene3dRef.current.highlightParticle(randomIndex)
                    }

                    playWinnerSound()
                    confetti({
                        particleCount: 200,
                        spread: 100,
                        origin: { y: 0.6 }
                    })
                }
            } else {
                if (isSelectingRef.current) {
                    isSelectingRef.current = false
                    setIsSelecting(false)
                    setSelectedName(null)
                    if (scene3dRef.current) {
                        scene3dRef.current.clearHighlight()
                    }
                }
            }
        }

        animationFrameRef.current = requestAnimationFrame(predictWebcam)
    }

    // Process gestures
    const processGesture = (gesture: string) => {
        if (gesture === lastGestureRef.current) {
            gestureCountRef.current++
        } else {
            gestureCountRef.current = 0
            lastGestureRef.current = gesture
            setLastGesture(gesture)
            // Debug log
            if (gesture !== 'None') {
                console.log('🤚 Detected gesture:', gesture)
            }
        }

        // Debounce: require at least 1 frame of consistency (gestureCount > 0)
        // This means gesture must be detected at least once (on second frame it will be > 0)
        if (gestureCountRef.current >= 0) {
            if (gesture === 'Open_Palm') {
                isSelectingRef.current = false
                setIsSelecting(false)
                isShufflingRef.current = true
                setIsShuffling(true)
                setSelectedName(null)
                if (scene3dRef.current) {
                    scene3dRef.current.clearHighlight()
                }
                setGestureIndicator('👋 Sphere Mode')
                setIndicatorColor('#4ade80') // Green

                if (scene3dRef.current && scene3dRef.current.particles) {
                    startHeartbeatLoop()
                    scene3dRef.current.transformTo('sphere')
                } else {
                    startChaosAnimation()
                }
            } else if (gesture === 'Closed_Fist') {
                setIsSelecting(false)
                setSelectedName(null)
                if (scene3dRef.current) {
                    scene3dRef.current.clearHighlight()
                    scene3dRef.current.transformTo('tree')
                }
                setGestureIndicator('🎄 Tree Mode')
                setIndicatorColor('#f87171') // Red
            } else if (gesture === 'Pointing_Up') {
                setGestureIndicator('👆 Picking...')
                setIndicatorColor('#fbbf24') // Yellow
            } else if (gesture === 'None') {
                setGestureIndicator('Waiting...')
                setIndicatorColor('#ccc') // Gray
            }
        }
    }

    // Cleanup
    useEffect(() => {
        return () => {
            webcamRunningRef.current = false
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current)
            }
            if (videoRef.current?.srcObject) {
                const stream = videoRef.current.srcObject as MediaStream
                stream.getTracks().forEach((track) => track.stop())
            }
            stopHeartbeatLoop()
        }
    }, [])

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
            <Navigation />

            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-5xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent mb-4">
                        Magic Random Name Picker
                    </h1>
                    <p className="text-xl text-purple-200">
                        Công cụ chọn tên ngẫu nhiên với điều khiển bằng cử chỉ tay
                    </p>
                </div>

                {/* Main Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Sidebar */}
                    <div className="lg:col-span-1 space-y-4">
                        {/* Status */}
                        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 border border-white/10 shadow-xl">
                            <p className="text-white text-sm font-medium">{statusText}</p>
                        </div>

                        {/* Camera */}
                        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 border border-white/10 shadow-xl">
                            <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-black mb-3 border-2 border-white/10">
                                <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    className="w-full h-full object-cover scale-x-[-1]"
                                    style={{ filter: 'brightness(1.2) saturate(1.1) contrast(0.95)' }}
                                />
                                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 px-3 py-1 rounded-xl text-xs" style={{ color: indicatorColor }}>
                                    {gestureIndicator}
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    const newState = !soundEnabled
                                    console.log('🔊 Sound toggle:', newState ? 'ON' : 'OFF')
                                    setSoundEnabled(newState)
                                    if (!newState) {
                                        // When turning off, stop all sounds
                                        stopHeartbeatLoop()
                                    } else {
                                        // When turning on, resume heartbeat if shuffling
                                        if (isShufflingRef.current && !isSelectingRef.current) {
                                            console.log('🎵 Resuming heartbeat loop...')
                                            startHeartbeatLoop()
                                        }
                                    }
                                }}
                                className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-all ${soundEnabled ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-600 hover:bg-gray-700'
                                    }`}
                            >
                                {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                                {soundEnabled ? 'Sound On' : 'Sound Off'}
                            </button>
                        </div>

                        {/* Upload */}
                        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 border border-white/10 shadow-xl">
                            <label className="block cursor-pointer">
                                <div className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center hover:border-purple-400 transition-all bg-white/5 hover:bg-purple-500/10">
                                    <Upload className="w-8 h-8 mx-auto mb-2 text-white/70" />
                                    <p className="text-sm text-white/70">Drop Excel File</p>
                                </div>
                                <input
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                                    className="hidden"
                                />
                            </label>

                            {/* Download Sample Button */}
                            <a
                                href="/mau_danh_sach.xlsx"
                                download="mau_danh_sach.xlsx"
                                className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm font-medium transition-all"
                            >
                                <Download className="w-4 h-4" />
                                Tải file mẫu Excel
                            </a>

                            <textarea
                                value={textareaValue}
                                placeholder="Or paste names here (one per line)..."
                                className="w-full h-24 mt-3 bg-black/20 border border-white/20 rounded-lg p-2 text-white text-sm placeholder-white/40 resize-none focus:outline-none focus:border-purple-400"
                                onChange={(e) => {
                                    setTextareaValue(e.target.value)
                                    setNames(parseNames(e.target.value))
                                }}
                            />

                            <button
                                onClick={startWebcam}
                                disabled={names.length < 2 || isRunning}
                                className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-lg text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
                            >
                                <Play className="w-5 h-5" />
                                Start Game
                            </button>
                        </div>
                    </div>

                    {/* Main Arena */}
                    <div className="lg:col-span-3">
                        <div className="relative bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden shadow-2xl" style={{ height: '600px' }}>
                            <div
                                ref={containerRef}
                                className="absolute inset-0"
                            />
                            {selectedName && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-50">
                                    <div
                                        className="text-7xl md:text-8xl lg:text-9xl font-black text-transparent bg-clip-text bg-gradient-to-b from-white via-yellow-200 to-yellow-500 animate-pulse px-4 text-center"
                                        style={{
                                            filter: 'drop-shadow(0 0 50px rgba(251,191,36,0.4))',
                                            lineHeight: '1.25',
                                            paddingBottom: '0.15em'
                                        }}
                                    >
                                        {selectedName}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Instructions */}
                <div className="mt-8 bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
                    <h3 className="text-2xl font-bold text-white mb-4">How to Use</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex items-start gap-3 bg-white/5 p-4 rounded-xl">
                            <div className="text-3xl">👋</div>
                            <div>
                                <h4 className="text-white font-semibold mb-1">Open Palm</h4>
                                <p className="text-purple-200 text-sm">Switch to Sphere Mode</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3 bg-white/5 p-4 rounded-xl">
                            <div className="text-3xl">✊</div>
                            <div>
                                <h4 className="text-white font-semibold mb-1">Closed Fist</h4>
                                <p className="text-purple-200 text-sm">Switch to Tree Mode</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3 bg-white/5 p-4 rounded-xl">
                            <div className="text-3xl">👆</div>
                            <div>
                                <h4 className="text-white font-semibold mb-1">Point Up</h4>
                                <p className="text-purple-200 text-sm">Pick a Random Winner</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default RandomNamePicker
